// Enhanced components for the new analytics

import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Shield, Target, Activity, DollarSign, BarChart3 } from 'lucide-react';

// VWAP Analysis Component
export function VWAPAnalysisCard({ data }: { data: any }) {
  const getTrendColor = () => {
    switch (data.trend) {
      case 'bullish': return '#10b981';
      case 'bearish': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getTrendIcon = () => {
    switch (data.trend) {
      case 'bullish': return <TrendingUp size={16} style={{ color: '#10b981' }} />;
      case 'bearish': return <TrendingDown size={16} style={{ color: '#ef4444' }} />;
      default: return <BarChart3 size={16} style={{ color: '#94a3b8' }} />;
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Target size={16} style={{ color: '#3b82f6' }} />
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          VWAP Analysis
        </h4>
        {getTrendIcon()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>VWAP</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>
            {data.currentVWAP.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Distance</div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: getTrendColor()
          }}>
            {data.distance > 0 ? '+' : ''}{data.distance.toFixed(2)}
          </div>
        </div>
      </div>

      <div style={{ 
        marginTop: '12px', 
        padding: '8px 12px', 
        backgroundColor: getTrendColor() + '20',
        borderRadius: '4px',
        border: `1px solid ${getTrendColor()}40`
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: getTrendColor(), fontWeight: '600' }}>
            {data.trend.toUpperCase()} TREND
          </span>
          <span style={{ fontSize: '11px', color: getTrendColor() }}>
            {data.distancePercent > 0 ? '+' : ''}{data.distancePercent.toFixed(2)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// Book Pressure Component
export function BookPressureCard({ data }: { data: any }) {
  const getPressureColor = () => {
    if (Math.abs(data.pressure) < 0.2) return '#94a3b8';
    return data.pressure > 0 ? '#10b981' : '#ef4444';
  };

  const getPressureStatus = () => {
    if (data.pressure > 0.3) return 'STRONG BUY PRESSURE';
    if (data.pressure > 0.1) return 'MODERATE BUY PRESSURE';
    if (data.pressure < -0.3) return 'STRONG SELL PRESSURE';
    if (data.pressure < -0.1) return 'MODERATE SELL PRESSURE';
    return 'BALANCED PRESSURE';
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Activity size={16} style={{ color: '#8b5cf6' }} />
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Book Pressure ({data.confidenceLevel} cf)
        </h4>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Buy Flow</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
            {data.buyPressure.toLocaleString()}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Sell Flow</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>
            {data.sellPressure.toLocaleString()}
          </div>
        </div>
      </div>

      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: getPressureColor() + '20',
        borderRadius: '4px',
        border: `1px solid ${getPressureColor()}40`,
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '11px', 
          color: getPressureColor(),
          fontWeight: '600'
        }}>
          {getPressureStatus()}
        </div>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: '700', 
          color: getPressureColor(),
          marginTop: '4px'
        }}>
          {data.pressure > 0 ? '+' : ''}{(data.pressure * 100).toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

// Spread Analysis Component
export function SpreadAnalysisCard({ data }: { data: any }) {
  const getCompetitivenessColor = () => {
    switch (data.competitiveness) {
      case 'tight': return '#10b981';
      case 'wide': return '#ef4444';
      default: return '#f59e0b';
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <DollarSign size={16} style={{ color: '#f59e0b' }} />
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Spread Analysis
        </h4>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Current</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>
            {data.currentSpread.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Percentile</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: getCompetitivenessColor() }}>
            {data.spreadPercentile.toFixed(0)}%
          </div>
        </div>
      </div>

      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: getCompetitivenessColor() + '20',
        borderRadius: '4px',
        border: `1px solid ${getCompetitivenessColor()}40`,
        marginBottom: '8px'
      }}>
        <div style={{ 
          fontSize: '11px', 
          color: getCompetitivenessColor(),
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {data.competitiveness.toUpperCase()} SPREAD
        </div>
      </div>

      <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>
        Optimal: {data.optimalBid.toFixed(2)} / {data.optimalAsk.toFixed(2)}
      </div>
    </div>
  );
}

// Risk Metrics Component
export function RiskMetricsCard({ data }: { data: any }) {
  // Normalize overallRisk to a string, handling cases where it might be a number or other type
  const normalizeOverallRisk = (risk: any): 'low' | 'medium' | 'high' => {
    if (typeof risk === 'string') {
      const lower = risk.toLowerCase();
      if (lower === 'low' || lower === 'medium' || lower === 'high') {
        return lower as 'low' | 'medium' | 'high';
      }
    }
    // If it's a number, convert to risk level
    if (typeof risk === 'number') {
      if (risk >= 0.7) return 'high';
      if (risk >= 0.4) return 'medium';
      return 'low';
    }
    // Default to 'low' if unknown
    return 'low';
  };

  const overallRisk = normalizeOverallRisk(data?.overallRisk);

  const getRiskColor = () => {
    switch (overallRisk) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getRiskIcon = () => {
    switch (overallRisk) {
      case 'low': return <Shield size={16} style={{ color: '#10b981' }} />;
      case 'medium': return <AlertTriangle size={16} style={{ color: '#f59e0b' }} />;
      case 'high': return <AlertTriangle size={16} style={{ color: '#ef4444' }} />;
      default: return <Shield size={16} style={{ color: '#94a3b8' }} />;
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {getRiskIcon()}
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Risk Assessment
        </h4>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '2px' }}>Inventory</div>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: data.inventoryRisk > 0.7 ? '#ef4444' : data.inventoryRisk > 0.4 ? '#f59e0b' : '#10b981'
          }}>
            {(data.inventoryRisk * 100).toFixed(0)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '2px' }}>Price</div>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: data.priceRisk > 0.7 ? '#ef4444' : data.priceRisk > 0.4 ? '#f59e0b' : '#10b981'
          }}>
            {(data.priceRisk * 100).toFixed(0)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '2px' }}>Liquidity</div>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: data.liquidityRisk > 0.7 ? '#ef4444' : data.liquidityRisk > 0.4 ? '#f59e0b' : '#10b981'
          }}>
            {(data.liquidityRisk * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: getRiskColor() + '20',
        borderRadius: '4px',
        border: `1px solid ${getRiskColor()}40`
      }}>
        <div style={{ 
          fontSize: '11px', 
          color: getRiskColor(),
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {overallRisk.toUpperCase()} RISK
        </div>
        <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center', marginTop: '4px' }}>
          Recommended Position: {data.recommendedPosition.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

// Trading Signals Component
export function TradingSignalsCard({ 
  vwapAnalysis, 
  bookPressure, 
  depthImbalance, 
  spreadAnalysis 
}: { 
  vwapAnalysis: any; 
  bookPressure: any; 
  depthImbalance: any; 
  spreadAnalysis: any; 
}) {
  // Generate actionable trading signals
  const signals = [];

  // VWAP Signal
  if (Math.abs(vwapAnalysis.distancePercent) > 0.5) {
    signals.push({
      type: vwapAnalysis.distance > 0 ? 'SELL' : 'BUY',
      reason: `Price ${vwapAnalysis.distancePercent.toFixed(1)}% ${vwapAnalysis.distance > 0 ? 'above' : 'below'} VWAP`,
      strength: Math.min(3, Math.abs(vwapAnalysis.distancePercent)),
      color: vwapAnalysis.distance > 0 ? '#ef4444' : '#10b981'
    });
  }

  // Book Pressure Signal
  if (Math.abs(bookPressure.pressure) > 0.2 && bookPressure.confidenceLevel !== 'low') {
    signals.push({
      type: bookPressure.pressure > 0 ? 'BUY' : 'SELL',
      reason: `${Math.abs(bookPressure.pressure * 100).toFixed(0)}% ${bookPressure.pressure > 0 ? 'buy' : 'sell'} pressure`,
      strength: Math.min(3, Math.abs(bookPressure.pressure) * 5),
      color: bookPressure.pressure > 0 ? '#10b981' : '#ef4444'
    });
  }

  // Depth Imbalance Signal
  if (Math.abs(depthImbalance.imbalancePercent) > 20) {
    signals.push({
      type: depthImbalance.imbalance > 0 ? 'BUY' : 'SELL',
      reason: `${Math.abs(depthImbalance.imbalancePercent).toFixed(0)}% depth imbalance`,
      strength: Math.min(3, Math.abs(depthImbalance.imbalancePercent) / 20),
      color: depthImbalance.imbalance > 0 ? '#10b981' : '#ef4444'
    });
  }

  // Spread Opportunity Signal
  if (spreadAnalysis.competitiveness === 'wide') {
    signals.push({
      type: 'MAKE',
      reason: `Wide spread - market making opportunity`,
      strength: 2,
      color: '#8b5cf6'
    });
  }

  const getStrengthBars = (strength: number) => {
    return Array.from({ length: 3 }, (_, i) => (
      <div
        key={i}
        style={{
          width: '8px',
          height: '8px',
          backgroundColor: i < strength ? '#10b981' : '#334155',
          borderRadius: '1px'
        }}
      />
    ));
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Target size={16} style={{ color: '#8b5cf6' }} />
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Trading Signals
        </h4>
      </div>

      {signals.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          color: '#94a3b8', 
          fontSize: '12px',
          padding: '16px 0'
        }}>
          No strong signals detected
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {signals.slice(0, 4).map((signal, index) => (
            <div
              key={index}
              style={{
                padding: '8px 12px',
                backgroundColor: signal.color + '15',
                borderRadius: '4px',
                border: `1px solid ${signal.color}30`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{ 
                  fontSize: '11px', 
                  fontWeight: '700', 
                  color: signal.color 
                }}>
                  {signal.type}
                </div>
                <div style={{ 
                  fontSize: '10px', 
                  color: '#94a3b8' 
                }}>
                  {signal.reason}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {getStrengthBars(signal.strength)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}